import * as d3 from 'd3';

document.addEventListener('alpine:init', () => {
    Alpine.data('publicationsChart', () => ({
        _ro: null,

        init() {
            this.$nextTick(() => {
                this.render();
                this._ro = new ResizeObserver(() => this.render());
                this._ro.observe(this.$el);
            });
        },

        destroy() {
            this._ro?.disconnect();
        },

        render() {
            const dataScript = this.$el.querySelector('.pub-chart-data');
            if (!dataScript) return;

            const allYears = JSON.parse(dataScript.textContent).filter(y => y !== null && y > 0);

            const yearFrom = parseInt(this.$el.dataset.yearFrom) || 2018;
            const yearToRaw = this.$el.dataset.yearTo;
            // When year_to is unset, cap at the last completed year — the
            // in-progress current year has no publications yet.
            const yearTo = yearToRaw ? parseInt(yearToRaw) : new Date().getFullYear() - 1;

            const counts = {};
            for (let y = yearFrom; y <= yearTo; y++) counts[y] = 0;
            allYears.forEach(y => {
                if (y >= yearFrom && y <= yearTo) counts[y] = (counts[y] || 0) + 1;
            });

            const data = Object.entries(counts).map(([year, count]) => ({
                year: +year,
                count,
            }));

            this.drawChart(data);
        },

        drawChart(data) {
            const svgEl = this.$refs.chart;
            const totalWidth = svgEl.clientWidth || 800;
            const totalHeight = 400;
            const margin = { top: 30, right: 20, bottom: 50, left: 50 };
            const width = totalWidth - margin.left - margin.right;
            const height = totalHeight - margin.top - margin.bottom;

            const ellisBlue = getComputedStyle(document.documentElement)
                .getPropertyValue('--color-ellis-blue').trim() || '#6a9bdd';

            const gray500 = getComputedStyle(document.documentElement)
                .getPropertyValue('--color-gray-500').trim() || '#6b7280';

            const ellisBlack = getComputedStyle(document.documentElement)
                .getPropertyValue('--color-ellis-black').trim() || '#171717';

            // Describe the chart for assistive technology.
            const total = d3.sum(data, d => d.count);
            const first = data[0]?.year;
            const last = data[data.length - 1]?.year;
            svgEl.setAttribute(
                'aria-label',
                `Bar chart of publications per year from ${first} to ${last}, ${total} publications in total.`
            );

            d3.select(svgEl).selectAll('*').remove();
            d3.select(svgEl).attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`);

            const chart = d3.select(svgEl)
                .append('g')
                .attr('transform', `translate(${margin.left},${margin.top})`);

            const x = d3.scaleBand()
                .domain(data.map(d => d.year))
                .range([0, width])
                .padding(0.25);

            const y = d3.scaleLinear()
                .domain([0, d3.max(data, d => d.count) || 1])
                .nice()
                .range([height, 0]);

            // Horizontal grid lines
            chart.append('g')
                .attr('class', 'grid')
                .call(
                    d3.axisLeft(y)
                        .ticks(5)
                        .tickSize(-width)
                        .tickFormat('')
                )
                .call(g => g.select('.domain').remove())
                .call(g => g.selectAll('.tick line')
                    .attr('stroke', '#f3f4f6')
                    .attr('stroke-width', 1));

            // X axis
            const xAxis = chart.append('g')
                .attr('transform', `translate(0,${height})`)
                .call(d3.axisBottom(x).tickFormat(d3.format('d')))
                .call(g => g.select('.domain').attr('stroke', '#e5e7eb'))
                .call(g => g.selectAll('.tick line').attr('stroke', '#e5e7eb'))
                .call(g => g.selectAll('text').attr('fill', gray500));

            if (data.length > 12) {
                xAxis.selectAll('text')
                    .attr('transform', 'rotate(-45)')
                    .style('text-anchor', 'end');
            }

            // Y axis
            chart.append('g')
                .call(d3.axisLeft(y).ticks(5).tickFormat(d3.format('d')))
                .call(g => g.select('.domain').attr('stroke', '#e5e7eb'))
                .call(g => g.selectAll('.tick line').attr('stroke', '#e5e7eb'))
                .call(g => g.selectAll('text').attr('fill', gray500));

            // Bars
            chart.selectAll('.bar')
                .data(data)
                .join('rect')
                .attr('class', 'bar')
                .attr('x', d => x(d.year))
                .attr('y', d => y(d.count))
                .attr('width', x.bandwidth())
                .attr('height', d => height - y(d.count))
                .attr('fill', ellisBlue)
                .attr('rx', 3);

            // Value labels above bars
            chart.selectAll('.bar-label')
                .data(data)
                .join('text')
                .attr('class', 'bar-label')
                .attr('x', d => x(d.year) + x.bandwidth() / 2)
                .attr('y', d => y(d.count) - 6)
                .attr('text-anchor', 'middle')
                .attr('fill', ellisBlack)
                .text(d => d.count > 0 ? d.count : '');
        },
    }));
});
